import {
  Controller,
  Post,
  Body,
  HttpCode,
  HttpStatus,
} from '@nestjs/common';
import { AutomationService } from '@/services/automation.service';
import type {
  TriggerExecutionDto,
  TriggerExecutionResponseDto,
} from '@/dto/trigger.dto';
import { ActionContext, ENUM_PHASE, ENUM_STATUS } from '@/client';
import { ApiExcludeController } from '@nestjs/swagger';
import { ERROR_CODE_PAIR } from '@/constants';

/**
 * AutomationController
 * 
 * 接收来自 Trigger Server 的自动化任务执行请求
 */
@ApiExcludeController()
@Controller('/__innerapi__/automation/invoke')
export class AutomationController {
  constructor(private readonly automationService: AutomationService) {}

  /**
   * Trigger Server 执行接口
   * 
   * POST /__innerapi__/automation/invoke
   * 
   * 执行流程：
   * 1. 验证请求参数完整性
   * 2. 构建任务执行上下文
   * 3. 调用 AutomationService 执行任务
   * 4. 返回执行结果
   * 
   * @param req - HTTP 请求对象
   * @param dto - 任务执行数据传输对象
   * @returns 任务执行响应
   */
  @Post()
  @HttpCode(HttpStatus.OK)
  async execute(
    @Body() dto: TriggerExecutionDto
  ): Promise<TriggerExecutionResponseDto> {
    try {
      const { trigger, triggerID, triggerType, instanceID, startAt, parameter = null } = dto;

      // 构建任务执行上下文
      const context: ActionContext = {
        attributes: {
          trigger: trigger ?? 'unknown',
          triggerID: triggerID ?? 'unknown',
          triggerType: triggerType ?? 'unknown',
          instanceID: instanceID ?? 'unknown',
          startAt: startAt ?? Date.now(),
        },
        content: JSON.stringify({ input: parameter }),
      };
      
      // 验证必填字段
      if (!trigger || !instanceID) {
        const responseCode = 400;
        const responseMessage = '缺少必填字段: trigger 或 instanceID 为空';
        const startTime = context.attributes.startAt;
        const endTime = Date.now();
        // 上报验证失败
        this.automationService.report(JSON.stringify({
          input: parameter,
          output: { error: responseMessage },
        }), {
          trigger: context.attributes.trigger,
          trigger_id: context.attributes.triggerID,
          trigger_type: context.attributes.triggerType,
          instance_id: context.attributes.instanceID,
          phase: ENUM_PHASE.Trigger,
          status: ENUM_STATUS.FAILED,
          start_at: startTime,
          end_at: endTime,
          duration_ms: startTime ? endTime - startTime : undefined,
        });
        return { code: responseCode, message: responseMessage };
      }

      // 异步执行任务，返回值为 触发 结果
      const ret = this.automationService.executeAction(
        trigger,
        instanceID,
        context,
      );

      // 处理执行结果
      if (ret.attributes.status === ENUM_STATUS.FAILED) {
        const output = JSON.parse(ret.content ?? '{}')?.output;
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          code: (ERROR_CODE_PAIR as any)[output?.error] ?? 500,
          message: output?.message ?? 'Internal error',
        };
      }
      
      return {
        code: 200,
        message: 'success',
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return {
        code: 500,
        message: `Internal error: ${errorMessage}`,
      };
    }
  }
}
